/**
 * Phase 8 T-20 §24.A — Step 3: Event Settings
 *
 * Four sub-sections:
 *   1. Duration — 15 / 30 / 45 / 60 min pill toggles
 *   2. Location — Google Meet / Zoom / Custom URL
 *   3. Required fields — name, email (always required) + optional phone, notes checkboxes
 *   4. Confirmation message — textarea
 */

import type { BookingConfig } from './BookingFlow';

const DURATIONS = [15, 30, 45, 60] as const;

const LOCATION_OPTIONS = [
  { value: 'google-meet', label: 'Google Meet' },
  { value: 'zoom', label: 'Zoom' },
  { value: 'custom', label: 'Custom URL' },
] as const;

const selectCls =
  'rounded-[8px] border px-2 py-1.5 text-[13px] vscode-border vscode-bg-secondary vscode-text-primary focus:outline-none focus:ring-1 focus:ring-[#10B981]';

interface EventSettingsStepProps {
  config: BookingConfig;
  onChange: (patch: Partial<BookingConfig>) => void;
}

export function EventSettingsStep({ config, onChange }: EventSettingsStepProps) {
  return (
    <div className="flex flex-col gap-6">
      {/* Duration */}
      <section>
        <h3 className="mb-3 text-[13px] font-semibold vscode-text-primary">Duration</h3>
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Interview duration">
          {DURATIONS.map((d) => (
            <button
              key={d}
              type="button"
              role="radio"
              aria-checked={config.durationMin === d}
              onClick={() => onChange({ durationMin: d })}
              className={[
                'rounded-[8px] border px-4 py-1.5 text-[13px] font-medium transition-colors',
                config.durationMin === d
                  ? 'border-[#10B981] bg-[#10B981]/10 text-[#10B981]'
                  : 'vscode-border vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary',
              ].join(' ')}
            >
              {d} min
            </button>
          ))}
        </div>
      </section>

      {/* Location */}
      <section>
        <h3 className="mb-3 text-[13px] font-semibold vscode-text-primary">Location</h3>
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Meeting location">
            {LOCATION_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={config.location === value}
                onClick={() => onChange({ location: value })}
                className={[
                  'rounded-[8px] border px-3 py-1.5 text-[13px] font-medium transition-colors',
                  config.location === value
                    ? 'border-[#10B981] bg-[#10B981]/10 text-[#10B981]'
                    : 'vscode-border vscode-text-secondary hover:vscode-bg-hover hover:vscode-text-primary',
                ].join(' ')}
              >
                {label}
              </button>
            ))}
          </div>
          {config.location === 'custom' && (
            <input
              type="url"
              value={config.customLocationUrl ?? ''}
              onChange={(e) => onChange({ customLocationUrl: e.target.value })}
              placeholder="https://meet.example.com/your-room"
              className="rounded-[8px] border px-3 py-1.5 text-[13px] vscode-border vscode-bg-secondary vscode-text-primary focus:outline-none focus:ring-1 focus:ring-[#10B981]"
              aria-label="Custom meeting URL"
            />
          )}
        </div>
      </section>

      {/* Required fields */}
      <section>
        <h3 className="mb-3 text-[13px] font-semibold vscode-text-primary">Required fields</h3>
        <div className="flex flex-col gap-2 rounded-[10px] border px-4 py-3 vscode-border vscode-bg-secondary">
          {/* Always required */}
          {(['Name', 'Email'] as const).map((field) => (
            <label key={field} className="flex items-center gap-2 text-[13px] vscode-text-secondary">
              <input
                type="checkbox"
                checked
                disabled
                className="accent-[#10B981] opacity-60"
                aria-label={`${field} (required)`}
              />
              {field}
              <span className="ml-1 text-[11px] vscode-text-tertiary">(required)</span>
            </label>
          ))}

          {/* Optional phone */}
          <label className="flex cursor-pointer items-center gap-2 text-[13px] vscode-text-secondary">
            <input
              type="checkbox"
              checked={config.requirePhone}
              onChange={(e) => onChange({ requirePhone: e.target.checked })}
              className="accent-[#10B981]"
              aria-label="Require phone number"
            />
            Phone
            <span className="ml-1 text-[11px] vscode-text-tertiary">(optional)</span>
          </label>

          {/* Optional notes */}
          <label className="flex cursor-pointer items-center gap-2 text-[13px] vscode-text-secondary">
            <input
              type="checkbox"
              checked={config.requireNotes}
              onChange={(e) => onChange({ requireNotes: e.target.checked })}
              className="accent-[#10B981]"
              aria-label="Require notes"
            />
            Notes / message
            <span className="ml-1 text-[11px] vscode-text-tertiary">(optional)</span>
          </label>
        </div>
      </section>

      {/* Confirmation message */}
      <section>
        <h3 className="mb-1.5 text-[13px] font-semibold vscode-text-primary">
          Confirmation message
        </h3>
        <p className="mb-2 text-[11px] vscode-text-tertiary">
          Shown to the candidate after they successfully book.
        </p>
        <textarea
          rows={4}
          value={config.confirmationMessage}
          onChange={(e) => onChange({ confirmationMessage: e.target.value })}
          placeholder="Thanks for booking! We'll be in touch with further details shortly."
          className="w-full resize-none rounded-[8px] border px-3 py-2 text-[13px] vscode-border vscode-bg-secondary vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none focus:ring-1 focus:ring-[#10B981]"
        />
        <div className="mt-1 text-right">
          <span className={`text-[11px] ${config.confirmationMessage.length > 300 ? 'text-red-500' : 'vscode-text-tertiary'}`}>
            {config.confirmationMessage.length}/300
          </span>
        </div>
      </section>
    </div>
  );
}
